import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsAdmin } from '@/features/auth/hooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { ApiError } from '@/lib/api-client';
import {
  useClinicalProtocolSimulatorRun,
  useDispatchClinicalProtocolSimulator,
} from '@/features/simulator/hooks';
import type { Pathway, SimulatorRunDetail } from '@/features/simulator/api';
import { SimulatorResultRenderer } from '@/features/simulator/components/SimulatorResultRenderer';
import { PageHero } from '@/components/layout/PageHero';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Clinical Protocol Simulator route. URL:
 *
 *   /simulators/clinical-protocol[?run_id=<n>]
 *
 * Two modes:
 *   - No run_id  → upload form (file + pathway picker → POST dispatch).
 *   - run_id set → poll the run, show stepper while running, render the
 *                  full report once complete.
 *
 * After dispatch, the route updates its own search params so refresh /
 * deep-link works. Same async-dispatch + polling shape as the four
 * agent flows; the only difference is the multipart upload up front.
 */

const searchSchema = z.object({
  run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const PATHWAY_OPTIONS: { value: Pathway; label: string }[] = [
  { value: 'drug', label: 'Drug (IND → NDA / 505(b)(2) / ANDA)' },
  { value: 'biologic', label: 'Biologic (IND → BLA / 351(k))' },
  { value: 'device', label: 'Medical Device (IDE → 510(k) / De Novo / PMA)' },
  { value: 'combination', label: 'Combination product (drug + device rules)' },
];

// Same 6-step / 12s rhythm the other agent flows use; tuned for the
// reviewer agent's typical 30-90s run window.
const SIMULATOR_WORKFLOW_STEPS = [
  'Extract protocol text',
  'Identify trial design + regulatory hooks',
  'Query openFDA + FDA guidance database',
  'Score findings against 21 CFR + ICH E6(R2)',
  'Classify Critical / Major / Minor',
  'Render review report',
] as const;

const SIMULATOR_STEP_SECONDS = 12;

export const Route = createFileRoute('/simulators_/clinical-protocol')({
  validateSearch: searchSchema,
  component: ClinicalProtocolSimulatorPage,
});

function ClinicalProtocolSimulatorPage() {
  const { run_id: runId } = Route.useSearch();
  const navigate = useNavigate();
  const runQuery = useClinicalProtocolSimulatorRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Clinical Protocol Simulator"
        subtitle="Pre-screen a draft clinical study protocol against 21 CFR 312 and ICH E6(R2) / E9."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-5">
        {runId === undefined && (
          <UploadPanel
            onDispatched={(newRunId) => {
              void navigate({
                to: '/simulators/clinical-protocol',
                search: { run_id: newRunId },
              });
            }}
          />
        )}

        {runId !== undefined && runQuery.isLoading && (
          <RunLoadingSkeleton />
        )}

        {runId !== undefined && runQuery.isError && (
          <ErrorCard message="Could not load this simulator run. It may have been deleted, or you may not have access." />
        )}

        {runId !== undefined && runQuery.data && (
          <RunStateBody
            run={runQuery.data}
            onStartAnother={() => void navigate({ to: '/simulators/clinical-protocol' })}
          />
        )}
      </main>
    </>
  );
}

// ── Upload panel (no run_id mode) ───────────────────────────────────────

function UploadPanel({
  onDispatched,
}: {
  onDispatched: (runId: number) => void;
}) {
  const dispatch = useDispatchClinicalProtocolSimulator();
  const isAdmin = useIsAdmin();
  const [pathway, setPathway] = useState<Pathway>('drug');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Pick a .docx file to upload.');
      return;
    }
    try {
      const res = await dispatch.mutateAsync({ file, pathway });
      onDispatched(res.simulator_run_id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Dispatch failed.',
      );
    }
  };

  return (
    <section className="bg-white border border-line rounded-2xl p-7 shadow-card">
      <h2 className="text-[18px] font-bold text-navy m-0 mb-2">
        Upload protocol
      </h2>
      <p className="text-[14px] text-muted leading-[1.55] m-0 mb-4">
        Pick the regulatory pathway, then upload your draft protocol as a{' '}
        <strong className="text-navy font-semibold">.docx</strong> Word
        document. An AI reviewer reads the protocol, consults openFDA + FDA
        guidance as needed, and returns findings classified Critical / Major /
        Minor with regulation citations.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted">
            Regulatory pathway
          </label>
          <Select value={pathway} onValueChange={(v) => setPathway(v as Pathway)}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PATHWAY_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="flex-1 min-w-0 py-2.5 px-3 border border-line-strong rounded-md bg-white text-[14px] text-ink file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-electric/10 file:text-navy file:font-semibold file:text-[12px] file:cursor-pointer hover:file:bg-electric/15"
          />
          <Button
            type="submit"
            variant="brand"
            disabled={dispatch.isPending || !file || !isAdmin}
            title={!isAdmin ? 'Admin only' : undefined}
            className="min-w-[160px]"
          >
            {dispatch.isPending ? 'Running analysis…' : 'Run analysis'}
          </Button>
        </div>

        <p className="text-[12px] text-muted m-0">
          .docx only. Max 10 MB.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-[10px] py-3 px-4 bg-state-error-bg border border-state-error-border text-state-error-text text-[14px] flex items-start gap-2.5"
          >
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </form>
    </section>
  );
}

// ── Run-state body (queued / running / failed / completed) ──────────────

function RunStateBody({
  run,
  onStartAnother,
}: {
  run: SimulatorRunDetail;
  onStartAnother: () => void;
}) {
  if (run.status === 'queued' || run.status === 'running') {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Protocol simulator run"
        />
        <AgentPollingPanel
          startedAt={run.started_at}
          workflowSteps={SIMULATOR_WORKFLOW_STEPS}
          stepSeconds={SIMULATOR_STEP_SECONDS}
          title="Reviewing protocol"
          subtitle="Reading the .docx, consulting openFDA + FDA guidance, and classifying issues against 21 CFR 312 + ICH E6(R2)."
        />
      </>
    );
  }

  if (run.status === 'failed') {
    return (
      <div className="bg-state-error-bg border-l-4 border-state-error-accent rounded-lg py-[18px] px-6 text-state-error-text">
        <h3 className="m-0 mb-1.5 text-[17px] font-bold">
          The review didn&rsquo;t complete
        </h3>
        <p className="text-[14px] leading-[1.55] m-0 mb-4">
          {run.error_message ||
            'The protocol reviewer did not complete. Try running it again with the same or a revised file.'}
        </p>
        <Button variant="outline" onClick={onStartAnother}>
          Run another protocol
        </Button>
      </div>
    );
  }

  if (run.simulator_response) {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Protocol simulator run"
        />
        <SimulatorResultRenderer report={run.simulator_response} />
        <ScrollToTopButton />
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={onStartAnother}>
            Run another protocol
          </Button>
        </div>
      </>
    );
  }

  // Completed but no payload (parse failure or similar) — surface clearly.
  return (
    <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-6 text-state-warning-text">
      <div className="font-bold text-[17px] mb-1">
        Run completed but no report was produced
      </div>
      <p className="text-[14px] m-0 mb-3">
        The agent finished but the response payload was missing or
        unparseable. Try uploading the document again.
      </p>
      <Button variant="outline" onClick={onStartAnother}>
        Run another protocol
      </Button>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6 text-state-error-text">
      {message}
    </div>
  );
}
